import { Button, Modal, Radio, RadioChangeEvent, Tooltip } from 'antd';
import React, { useState, useEffect } from 'react';
import { strings } from '../../../lang';
import TextArea from 'antd/es/input/TextArea';
import { PhoneOutlined } from '@ant-design/icons';
import { useLoadingGuard } from '../../../hooks/use-loading-guard';

export interface WrongPhoneNumberModalProps {
  phoneNumber: string;
  leadPhoneNumber: string;
  open: boolean;
  onOk: (message: string) => void;
  showModal: (val: boolean) => void;
  onUpdatePhoneNumber: (phoneNumber: string) => void;
  onCallButtonClick: (phoneNumber: string) => void;
  disableCallButton?: boolean;
  /** Autodialer flow: changes the submit button label to "Save & Complete Case" and skips the open-next-case wording. */
  isAutodialerFlow?: boolean;
}

const WrongPhoneNumberModal: React.FC<WrongPhoneNumberModalProps> = ({
  open,
  phoneNumber,
  leadPhoneNumber,
  onOk,
  showModal,
  onUpdatePhoneNumber,
  onCallButtonClick,
  disableCallButton,
  isAutodialerFlow,
}) => {
  // Autodialer (and any other caller that disables outbound calls) must skip the
  // "try the alternate lead phone" detour entirely. Otherwise the call button stays
  // disabled while the footer buttons that depend on `isNumberCalled` also stay
  // disabled, leaving Cancel as the only exit.
  const shouldOfferLeadPhone = !disableCallButton && leadPhoneNumber !== '' && leadPhoneNumber !== phoneNumber;

  const [selectedOption, setSelectedOption] = useState<string | null>(null);
  const [showTextInput, setShowTextInput] = useState<boolean>(false);
  const [freeText, setFreeText] = useState<string | null>(null);
  const [isLoading, guard] = useLoadingGuard();
  const [showLeadPhone, setShowLeadPhone] = useState<boolean>(shouldOfferLeadPhone);
  const [isNumberCalled, setIsNumberCalled] = useState<boolean>(false);
  const [isCallButtonDisabled, setIsCallButtonDisabled] = useState<boolean>(false);

  useEffect(() => {
    if (open) {
      setSelectedOption(null);
      setShowTextInput(false);
      setFreeText(null);
      setIsNumberCalled(false);
      setShowLeadPhone(shouldOfferLeadPhone);
    }
  }, [open, shouldOfferLeadPhone]);

  const disableCallButtonTemporarily = () => {
    setIsCallButtonDisabled(true);
    setTimeout(() => {
      setIsCallButtonDisabled(false);
    }, 5000);
  };

  const handleSubmit = () =>
    guard(async () => {
      await onOk(!freeText ? `Wrong Phone Number: ${selectedOption!}` : `Wrong Phone Number: ${selectedOption}${freeText!}`);
      setFreeText(null);
      setShowTextInput(false);
      setSelectedOption(null);
    });

  const handleCancel = () => {
    showModal(false);
    setIsNumberCalled(false);
    setSelectedOption(null);
    setShowTextInput(false);
    setFreeText(null);
    setShowLeadPhone(shouldOfferLeadPhone);
  };

  const onRadioChange = (e: RadioChangeEvent) => {
    setShowTextInput(e.target.id === '4');
    setSelectedOption(e.target.value);
  };

  const onTextFieldChanged = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setFreeText(e.target.value);
  };

  const updatePhoneNumber = () =>
    guard(async () => {
      await onUpdatePhoneNumber(leadPhoneNumber);
      handleCancel();
      setShowLeadPhone(false);
    });

  const onCallButton = () => {
    onCallButtonClick(leadPhoneNumber);
    disableCallButtonTemporarily();
    setIsNumberCalled(true);
  };

  const updateWrongPhoneNumberBody = () => {
    return (
      <div style={{ marginBottom: '20px' }}>
        <p>{strings.updateWrongPhoneNumber?.body}</p>
        <Button color="default" variant="outlined" onClick={onCallButton} disabled={isCallButtonDisabled || disableCallButton}>
          <PhoneOutlined style={{ color: 'green' }} /> {strings.startCall}
        </Button>
      </div>
    );
  };

  const closeCaseWrongPhoneNumberBody = () => {
    return (
      <>
        <p>* {strings.wrongPhoneNumber.prompt}</p>
        <Radio.Group onChange={onRadioChange} value={selectedOption}>
          <Radio id="1" value="Number looks invalid (e.g. 000000000 or 123456789)" style={{ marginBottom: '5px' }}>
            {strings.wrongPhoneNumber.reasons[0]}
          </Radio>
          <Radio id="2" value="No connection under this number" style={{ marginBottom: '5px' }}>
            {strings.wrongPhoneNumber.reasons[1]}
          </Radio>
          <Radio id="3" value="Wrong Person (e.g. did not know about Enpal)" style={{ marginBottom: '5px' }}>
            {strings.wrongPhoneNumber.reasons[2]}
          </Radio>
          <Radio id="4" value="Other: " style={{ marginBottom: '5px' }}>
            {strings.wrongPhoneNumber.reasons[3]}
          </Radio>
        </Radio.Group>
        {showTextInput && <TextArea placeholder={strings.wrongPhoneNumber.pleaseSpecifyReason} onChange={onTextFieldChanged} value={freeText || ''} maxLength={2000} showCount style={{ marginBottom: '20px' }} />}
        {showTextInput && freeText && freeText.length === 2000 && (
          <div
            style={{
              color: 'var(--error-color)',
              fontSize: '12px',
              marginBottom: '10px',
              fontWeight: 'bold',
            }}
          >
            {strings.wrongPhoneNumber.characterLimitReached}
          </div>
        )}
      </>
    );
  };

  return (
    <Modal
      title={strings.wrongPhoneNumber.title}
      width={600}
      centered
      closable={false}
      keyboard={false}
      maskClosable={false}
      open={open}
      footer={
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '5px' }}>
          <Button key="cancel" onClick={handleCancel} disabled={isLoading}>
            {strings.updateWrongPhoneNumber.cancel}
          </Button>
          {showLeadPhone ? (
            <>
              <Tooltip title={isNumberCalled ? '' : strings.updateWrongPhoneNumber.actionIsDisabled}>
                <Button disabled={isLoading || !isNumberCalled} onClick={() => setShowLeadPhone(false)}>
                  {strings.updateWrongPhoneNumber.newNumberIncorrect}
                </Button>
              </Tooltip>
              <Tooltip title={isNumberCalled ? strings.updateWrongPhoneNumber.updateWithnewNumberTooltip : strings.updateWrongPhoneNumber.actionIsDisabled}>
                <Button type="primary" disabled={!isNumberCalled} loading={isLoading} onClick={async () => await updatePhoneNumber()}>
                  {strings.updateWrongPhoneNumber.updateWithNewNumber}
                </Button>
              </Tooltip>
            </>
          ) : (
            <Button
              key="ok"
              type="primary"
              onClick={handleSubmit}
              title={strings.wrongPhoneNumber.buttonClarification}
              loading={isLoading}
              disabled={(!selectedOption && !showTextInput) || (showTextInput && !freeText)}
            >
              {isAutodialerFlow ? strings.wrongPhoneNumber.saveAndCompleteCase : strings.wrongPhoneNumber.saveAndOpenNextCase}
            </Button>
          )}
        </div>
      }
    >
      {showLeadPhone ? updateWrongPhoneNumberBody() : closeCaseWrongPhoneNumberBody()}
    </Modal>
  );
};

export default WrongPhoneNumberModal;
